import React from 'react';
import StoryTabsBar from './StoryTabsBar';
import ResourceRowsPanel from './ResourceRowsPanel';
import { STORY_VIEWS } from '../../../data/learningZoneMockData';

/**
 * Stories > Balgopal / Saint / 2 min Balgruha Stories (and every other row in Stories): the Stories
 * screen - a "Stories" heading, the list card with its strip of story tiles, then the selected
 * story's resource rows next to "Your Practice" - showing the row's published lessons.
 * `screen.preset` (a STORY_VIEWS id) picks that list's options: the header style, whether tiles
 * show coins, numbered headings, its description and icon; other rows use their sidebar icon
 * (`screen.icon`). CourseContentView keeps the behaviour and passes it in, as for ActionSongsView.
 */
export default function StoriesView({ title, screen = {}, lessons, activeId, onSelect, onOpenItem, quiz, practice }) {
  const { description = '', icon, numberedHeadings, standaloneHeader, hideTileCoins } = STORY_VIEWS[screen.preset] || {};
  const activeIndex = Math.max(0, lessons.findIndex((story) => story.id === activeId));
  const activeStory = lessons[activeIndex];
  // "Story 6 - Gopal's garland"; a title with its own number ("Balgopal Story 1") stays as it is.
  const number = numberedHeadings && activeStory && !/\d/.test(activeStory.title) ? activeIndex + 1 : undefined;
  // The Stories task type row would repeat the page heading.
  const showListHeader = !standaloneHeader && title.trim().toLowerCase() !== 'stories';

  return (
    <>
      <div className="flex items-center gap-4 mb-6">
        <span className="text-5xl leading-none" aria-hidden="true">
          📖
        </span>
        <div>
          <h1 className="text-[28px] font-extrabold text-slate-900 leading-tight">{standaloneHeader ? title : 'Stories'}</h1>
          <p className="text-[15px] font-medium text-gray-500">
            {standaloneHeader ? description : 'Read. Listen. Watch. Learn. Be Inspired!'}
          </p>
        </div>
      </div>

      <section aria-label={title} className="mb-6 bg-white/70 rounded-2xl border border-gray-100 p-4">
        {showListHeader && (
          <div className="flex items-center gap-3 mb-4">
            <span className="text-3xl leading-none" aria-hidden="true">
              {icon || screen.icon || '📖'}
            </span>
            <div>
              <h2 className="text-xl font-extrabold text-slate-900 leading-tight">{title}</h2>
              {description && <p className="text-sm font-medium text-gray-500">{description}</p>}
            </div>
          </div>
        )}

        {activeStory ? (
          <>
            <StoryTabsBar stories={lessons} activeStoryId={activeStory.id} onSelectStory={onSelect} showCoins={!hideTileCoins} />
          </>
        ) : (
          <p className="py-6 text-center text-[15px] font-semibold text-gray-500">No lessons have been added here yet.</p>
        )}
      </section>

      {activeStory && (
        <div className="grid grid-cols-1 [@container_(min-width:900px)]:grid-cols-[minmax(0,1fr)_minmax(360px,33.333%)] gap-6 items-start">
          <div className="min-w-0">
            {quiz || (
              <ResourceRowsPanel
                chapter={activeStory}
                heading={number ? `Story ${number} - ${activeStory.title}` : activeStory.title}
                rowPrefix={number ? `Story ${number}` : activeStory.title}
                onOpenItem={onOpenItem}
              />
            )}
          </div>
          {practice}
        </div>
      )}
    </>
  );
}
